/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement, ReactNode} from 'react';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '../../types/types';

export interface FormLabelValueType {
  label: string;
  value: string;
  children?: ReactNode;
  fullWidth?: boolean;
  halfWidth?: boolean;
  thirdWidth?: boolean;
  customWidth?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  disableMinWidth?: boolean;
  checkbox?: ReactElement
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  formLabel : {
    display                      : 'flex',
    padding                      : '13px',
    justifyContent               : 'flex-start',
    flexDirection                : 'column',
    alignItems                   : 'flex-start',
    height                       : '69px',
    background                   : dlsColor('grey8'),
    borderBottom                 : `1px solid ${dlsColor('grey6')}`,
    [theme.breakpoints.up('lg')] : {
      minWidth : ({disableMinWidth}: {disableMinWidth: boolean}) => disableMinWidth ? 'auto' : '400px'
    },
    [theme.breakpoints.down('sm')] : {
      minWidth  : 'none',
      maxWidth  : 'none',
      flexBasis : '100%'
    }
  },
  labelStyle : {
    margin        : 0,
    fontFamily    : 'Roboto',
    fontStyle     : 'normal',
    fontWeight    : 'bold',
    fontSize      : '14px',
    lineHeight    : '18px',
    letterSpacing : '1px',
    textTransform : 'uppercase'
  },
  valueStyle : {
    margin       : 0,
    width        : '400px',
    fontFamily   : 'Roboto',
    fontStyle    : 'normal',
    fontWeight   : 'normal',
    fontSize     : '16px',
    lineHeight   : '24px',
    alignItems   : 'center',
    whiteSpace   : 'nowrap',
    overflow     : 'hidden',
    textOverflow : 'ellipsis'
  }
}));

/**
 * Component that wraps its child component(s) with styles that are specific to a Form.
 *
 * @param children        Component that gets wrapped
 * @param fullWidth       Should the grid item be full width; takes precedence
 *                        over halfWidth, thirdWidth and width if specified.
 * @param halfWidth       Should the grid item be half width (default); takes
 *                        precedence over thirdWidth and width if specified.
 * @param thirdWidth      Should the grid item be third width; takes precedence
 *                        over width if specified.
 * @param customWidth     Should the grid item be a specific custom width.
 * @param disableMinWidth Should be specified if any of the above width
 *                        properties are specified.
 *
 * ```typescript
 *
 * import {FormLabelValue} from '@ion/dls/src/components/forms/';
 *
 * <FormLabelValue>
 *   <Component />
 * </FormLabelValue>
 *
 * ```
 */
export const FormLabelValue = ({
  children,
  label,
  value,
  customWidth = 6,
  fullWidth = false,
  halfWidth = false,
  thirdWidth = false,
  disableMinWidth = false,
  ...props
}: FormLabelValueType) => {
  const classes = useStyles({disableMinWidth});
  const md = fullWidth ? 12 : (halfWidth ? 6 : (thirdWidth ? 4 : customWidth));
  const xs = 12;
  return (
    <Grid item
      md={md} xs={xs}
      classes={{
        root : classes.formLabel
      }}>
      <>
        <p className={classes.labelStyle}>{label}</p>
        {value && <p className={classes.valueStyle}>{value}</p>}
        {props.checkbox && props.checkbox}
      </>
    </Grid>
  );
};

export default FormLabelValue;
